import { Trans, t } from "@lingui/macro";
import { DragEvent, useState } from "react";
import { Fire } from "../../../services/FireClient";
import { Button } from "../../../components/ui/button";
import { Progress } from "../../../components/ui/progress";
import { useToast } from "../../../hooks/use-toast";
import { Upload } from "lucide-react";

export const UploadFileSection: React.FC<{ storageFolderPath: string, onFilesUploaded: (files: UploadedFile[]) => void }> = ({ storageFolderPath, onFilesUploaded }) => {
  const { toast } = useToast();
  const [inProgress, setInProgress] = useState(false);
  const [inProgressPercent, setInProgressPercent] = useState(0);
  const [dragIsOver, setDragIsOver] = useState(false);

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragIsOver(true);
  };
  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragIsOver(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragIsOver(false);
    const droppedFiles = Array.from(event.dataTransfer.files);
    filesSelected({ target: { files: droppedFiles } })
  };

  return <div className="space-y-4">
    {inProgress && (
      <div className="w-full">
        <Progress value={inProgressPercent} className="h-2" />
        <p className="text-xs text-center mt-1 text-muted-foreground">{Math.round(inProgressPercent)}%</p>
      </div>
    )}
    {!inProgress && <>
      <div
        className={`border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center transition-colors cursor-pointer ${dragIsOver ? 'border-primary bg-primary/10' : 'border-border bg-muted/50 hover:bg-muted'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById('bot-file-input')?.click()}
      >
        <Upload className="w-10 h-10 text-muted-foreground mb-2" />
        <p className="text-sm text-card-foreground font-medium"><Trans>Drop your PDF files here</Trans></p>
        <p className="text-xs text-muted-foreground mt-1"><Trans>Or click to browse</Trans></p>
      </div>
    </>}
    <input id="bot-file-input" type='file' onChange={filesSelected} hidden accept='.pdf' multiple />
  </div>

  async function filesSelected(e: any) {
    const selectedFiles = e.target.files as File[];
    if (selectedFiles && selectedFiles.length > 0) {
      const filteredFiles = Array.from(selectedFiles).filter((f: File) => f.type === 'application/pdf');
      if (filteredFiles.length !== e.target.files.length || e.target.files.length === 0) {
        toast({
          description: t`Non-PDF files have been excluded.`,
        });
      }

      if (filteredFiles.length > 0) {
        setInProgress(true);
        const files = await uploadFilesAndGetSourceFiles(filteredFiles);
        setInProgress(false);
        onFilesUploaded(files);
      }
    }
  }

  async function uploadFilesAndGetSourceFiles(files: File[]): Promise<UploadedFile[]> {
    let uploadedFiles: UploadedFile[] = [];
    return new Promise(async (resolve, reject) => {
      for (let index = 0; index < files.length; index++) {
        let file = files[index];
        setInProgress(true);
        Fire.uploadFile(file, `${storageFolderPath}/${file.name}`, (url) => {
          if (url) {
            uploadedFiles.push(new UploadedFile(file.name, url, file.size, file.type));
            if (uploadedFiles.length === files.length) {
              resolve(uploadedFiles);
            }
          } else {
            resolve([]);
          }
        }, (progress) => {
          setInProgressPercent(progress);
        });
      }
    });
  }
}

export class UploadedFile {
  constructor(public fileName: string, public fileUrl: string, public fileSize: number, public fileType: string) { }
}